import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { habitService, Habit, HabitCompletion, HabitStats, HabitStreak, DetailedHabitStats, RecordCompletionData } from '../../services/habitService';
import CompletionValueModal from './CompletionValueModal';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import './HabitDetail.css';

interface HabitDetailProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const HabitDetail: React.FC<HabitDetailProps> = ({ user, onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [habit, setHabit] = useState<Habit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<HabitStats | null>(null);
  const [streak, setStreak] = useState<HabitStreak | null>(null);
  const [detailedStats, setDetailedStats] = useState<DetailedHabitStats['stats'] | null>(null);
  const [completions, setCompletions] = useState<HabitCompletion[]>([]);
  const [chartData, setChartData] = useState<{ date: string, score: number }[]>([]);
  const [historyData, setHistoryData] = useState<{ date: string, completions: number }[]>([]);
  const [measurementData, setMeasurementData] = useState<{ date: string, value: number }[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month'>('week');
  const [showCalendarEdit, setShowCalendarEdit] = useState(false);
  const [calendarData, setCalendarData] = useState<{ [key: string]: boolean }>({});
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [completionModalState, setCompletionModalState] = useState<{
    habit: Habit;
    date: string;
  } | null>(null);


  const categories = [
    { value: 'health', label: 'Health' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'education', label: 'Education' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'social', label: 'Social' },
    { value: 'hobbies', label: 'Hobbies' },
    { value: 'self_care', label: 'Self Care' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'sleep', label: 'Sleep' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (id && user?.id) {
      loadHabitData();
    }
  }, [id, user]);

  useEffect(() => {
    if (completions.length > 0) {
      generateChartData(completions);
      generateCalendarData(completions);
    } else {
      // Clear calendar data when no completions
      setCalendarData({});
    }
    // Reload detailed stats when period changes
    if (id && user?.id) {
      loadDetailedStats();
    }
  }, [selectedPeriod, completions]);

  const loadDetailedStats = async () => {
    if (!id || !user?.id) return;

    try {
      const habitId = parseInt(id);
      const days = selectedPeriod === 'week' ? 7 : 30; // Month view uses 30 days for detailed stats

      const detailedStatsResponse = await habitService.getDetailedHabitStats(habitId, user.id, days);

      if (detailedStatsResponse.success && detailedStatsResponse.stats) {
        setDetailedStats(detailedStatsResponse.stats);
      }
    } catch (err) {
      console.error('Error loading detailed stats:', err);
    }
  };

  const loadHabitData = async () => {
    if (!id || !user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const habitId = parseInt(id);

      // Get date range for calendar data (last 3 months to ensure we have enough data)
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      const startDate = threeMonthsAgo.toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];

      // Load habit details, stats, streak, detailed stats, and completions by date range in parallel
      const [habitResponse, statsResponse, streakResponse, detailedStatsResponse, completionsByDateResponse] = await Promise.all([
        habitService.getHabitById(habitId),
        habitService.getCompletionStats(user.id, habitId, undefined, undefined, 30),
        habitService.getHabitStreak(habitId, user.id),
        habitService.getDetailedHabitStats(habitId, user.id, selectedPeriod === 'week' ? 7 : 30),
        habitService.getCompletionsByDateRange(user.id, startDate, endDate)
      ]);

      if (habitResponse.success && habitResponse.habit) {
        setHabit(habitResponse.habit);
      } else {
        setError(habitResponse.message || 'Failed to load habit');
      }

      if (statsResponse.success && statsResponse.stats) {
        setStats(statsResponse.stats);
      }

      if (streakResponse.success && streakResponse.streak) {
        setStreak(streakResponse.streak);
      }

      if (detailedStatsResponse.success && detailedStatsResponse.stats) {
        setDetailedStats(detailedStatsResponse.stats);
      }

      if (completionsByDateResponse.success && completionsByDateResponse.completions_by_date) {
        // Process completions data similar to HabitTracker
        const completionsByDate = completionsByDateResponse.completions_by_date;
        const habitCompletions: HabitCompletion[] = [];

        // Convert completions_by_date format to array format for chart generation
        Object.keys(completionsByDate).forEach(dateStr => {
          const dayCompletions = completionsByDate[dateStr] || [];
          const habitCompletion = dayCompletions.find(c => Number(c.habit_id) === Number(habitId));
          if (habitCompletion) {
            console.log('Raw habit completion:', habitCompletion);
            habitCompletions.push({
              ...habitCompletion,
              is_completed: Boolean(habitCompletion.is_completed),
              target_achieved: Boolean(habitCompletion.target_achieved)
            });
          }
        });

        setCompletions(habitCompletions);
        // Generate chart data from completions
        generateChartData(habitCompletions);
        // Generate calendar data from completions_by_date
        generateCalendarDataFromDateRange(completionsByDate, habitId);
      }
    } catch (err) {
      console.error('Error loading habit data:', err);
      setError('Failed to load habit data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (completions: HabitCompletion[]) => {
    const scoreData: { date: string, score: number }[] = [];
    const historyData: { date: string, completions: number }[] = [];
    const measureData: { date: string, value: number }[] = [];

    const today = new Date();

    if (selectedPeriod === 'week') {
      // For weekly view, group completions by week
      const weeksToShow = 4; // Show last 4 weeks including current week

      for (let i = weeksToShow - 1; i >= 0; i--) {
        // Calculate the start of each week going backwards from current week
        const currentWeekStart = new Date(today);
        currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay()); // Start of current week (Sunday)

        const weekStart = new Date(currentWeekStart);
        weekStart.setDate(weekStart.getDate() - (i * 7)); // Go back i weeks
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6); // End of week (Saturday)

        const weekStartStr = weekStart.toISOString().split('T')[0];
        const weekEndStr = weekEnd.toISOString().split('T')[0];

        // Count completions for this week
        const weekCompletions = completions.filter(c => {
          const completionDate = c.completion_date.split('T')[0];
          return completionDate >= weekStartStr && completionDate <= weekEndStr && c.target_achieved;
        }).length;

        // Calculate average score for this week
        const weekCompletionsWithScore = completions.filter(c => {
          const completionDate = c.completion_date.split('T')[0];
          return completionDate >= weekStartStr && completionDate <= weekEndStr;
        });

        const avgScore = weekCompletionsWithScore.length > 0
          ? weekCompletionsWithScore.reduce((sum, c) => sum + (c.achievement_percentage || 0), 0) / weekCompletionsWithScore.length
          : 0;

        // Calculate average measurement for this week
        const weekCompletionsWithMeasurement = completions.filter(c => {
          const completionDate = c.completion_date.split('T')[0];
          const hasMeasurement = c.measurement_value !== undefined && c.measurement_value !== null && !isNaN(Number(c.measurement_value));
          return completionDate >= weekStartStr && completionDate <= weekEndStr && hasMeasurement;
        });

        console.log('Week completions with measurement:', weekCompletionsWithMeasurement);

        let avgMeasurement = 0;
        if (weekCompletionsWithMeasurement.length > 0) {
          const sum = weekCompletionsWithMeasurement.reduce((acc, c) => {
            const val = Number(c.measurement_value);
            return acc + (isNaN(val) ? 0 : val);
          }, 0);
          avgMeasurement = sum / weekCompletionsWithMeasurement.length;
        }

        console.log('Average measurement for week:', avgMeasurement);

        scoreData.push({ date: weekStartStr, score: Math.round(avgScore) });
        historyData.push({ date: weekStartStr, completions: weekCompletions });
        measureData.push({ date: weekStartStr, value: isNaN(avgMeasurement) ? 0 : Number(avgMeasurement.toFixed(1)) });
      }
    } else if (selectedPeriod === 'month') {
      // For monthly view, group completions by month
      const monthsToShow = 6; // Show last 6 months including current month

      for (let i = monthsToShow - 1; i >= 0; i--) {
        // Calculate each month going backwards from current month
        const monthStart = new Date(today);
        monthStart.setDate(1); // First day of month
        monthStart.setMonth(today.getMonth() - i); // Go back i months

        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1); // Next month
        monthEnd.setDate(0); // Last day of the current month

        const monthStartStr = monthStart.toISOString().split('T')[0];
        const monthEndStr = monthEnd.toISOString().split('T')[0];

        // Count completions for this month
        const monthCompletions = completions.filter(c => {
          const completionDate = c.completion_date.split('T')[0];
          return completionDate >= monthStartStr && completionDate <= monthEndStr && c.target_achieved;
        }).length;

        // Calculate average score for this month
        const monthCompletionsWithScore = completions.filter(c => {
          const completionDate = c.completion_date.split('T')[0];
          return completionDate >= monthStartStr && completionDate <= monthEndStr;
        });

        const avgScore = monthCompletionsWithScore.length > 0
          ? monthCompletionsWithScore.reduce((sum, c) => sum + (c.achievement_percentage || 0), 0) / monthCompletionsWithScore.length
          : 0;

        // Calculate average measurement for this month
        const monthCompletionsWithMeasurement = completions.filter(c => {
          const completionDate = c.completion_date.split('T')[0];
          const hasMeasurement = c.measurement_value !== undefined && c.measurement_value !== null && !isNaN(Number(c.measurement_value));
          return completionDate >= monthStartStr && completionDate <= monthEndStr && hasMeasurement;
        });

        console.log('Month completions with measurement:', monthCompletionsWithMeasurement);

        let avgMeasurement = 0;
        if (monthCompletionsWithMeasurement.length > 0) {
          const sum = monthCompletionsWithMeasurement.reduce((acc, c) => {
            const val = Number(c.measurement_value);
            return acc + (isNaN(val) ? 0 : val);
          }, 0);
          avgMeasurement = sum / monthCompletionsWithMeasurement.length;
        }

        console.log('Average measurement for month:', avgMeasurement);

        scoreData.push({ date: monthStartStr, score: Math.round(avgScore) });
        historyData.push({ date: monthStartStr, completions: monthCompletions });
        measureData.push({ date: monthStartStr, value: isNaN(avgMeasurement) ? 0 : Number(avgMeasurement.toFixed(1)) });
      }
    }

    setChartData(scoreData);
    setHistoryData(historyData);
    setMeasurementData(measureData);
  };

  const generateCalendarData = (completions: HabitCompletion[]) => {
    const calendarMap: { [key: string]: boolean } = {};

    completions.forEach(completion => {
      const dateStr = completion.completion_date.split('T')[0];
      // Convert to boolean - handle both boolean and number types
      const isCompleted = Boolean(completion.target_achieved) || Boolean(completion.is_completed);
      calendarMap[dateStr] = isCompleted;
    });

    setCalendarData(calendarMap);
  };

  const generateCalendarDataFromDateRange = (completionsByDate: { [date: string]: any[] }, habitId: number) => {
    const calendarMap: { [key: string]: boolean } = {};

    Object.keys(completionsByDate).forEach(dateStr => {
      const dayCompletions = completionsByDate[dateStr] || [];
      const habitCompletion = dayCompletions.find(c => Number(c.habit_id) === Number(habitId));

      if (habitCompletion) {
        // Use the same logic as HabitTracker for consistency
        const isCompleted = Boolean(habitCompletion.is_completed);
        calendarMap[dateStr] = isCompleted;
      }
    });

    setCalendarData(calendarMap);
  };


  const handleToggleStatus = async () => {
    if (!habit) return;

    try {
      const newStatus = !habit.is_active;
      const response = await habitService.updateHabit(habit.id, { is_active: newStatus });

      if (response.success) {
        setHabit(prev => prev ? { ...prev, is_active: newStatus } : null);
      } else {
        setError(response.message || 'Failed to update habit status');
      }
    } catch (err) {
      console.error('Error updating habit status:', err);
      setError('Failed to update habit status. Please try again.');
    }
  };

  const handleDeleteHabit = async () => {
    if (!habit || !window.confirm('Are you sure you want to delete this habit? This action cannot be undone.')) return;

    try {
      const response = await habitService.deleteHabit(habit.id);

      if (response.success) {
        navigate('/habits');
      } else {
        setError(response.message || 'Failed to delete habit');
      }
    } catch (err) {
      console.error('Error deleting habit:', err);
      setError('Failed to delete habit. Please try again.');
    }
  };

  const handleCalendarDayClick = async (dateStr: string) => {
    if (!habit || !user) return;

    // Check if this is a numeric tracking habit
    const isNumericTracking = habit.tracking_type === 'count' ||
      habit.tracking_type === 'measurement' ||
      habit.tracking_type === 'both';

    if (isNumericTracking) {
      setCompletionModalState({ habit, date: dateStr });
      return;
    }

    const isCurrentlyCompleted = calendarData[dateStr] || false;

    try {
      // Use the same approach as HabitTracker - always call recordCompletion with the opposite state
      const response = await habitService.recordCompletion({
        habit_id: habit.id,
        user_id: user.id,
        completion_date: dateStr,
        is_completed: !isCurrentlyCompleted
      });

      if (response.success) {
        // Update calendar data
        setCalendarData(prev => {
          const newData = { ...prev };
          if (!isCurrentlyCompleted) {
            newData[dateStr] = true;
          } else {
            delete newData[dateStr];
          }
          return newData;
        });

        // Update completions list
        if (!isCurrentlyCompleted) {
          // Add new completion
          const newCompletion: HabitCompletion = {
            id: Date.now(), // Temporary ID
            habit_id: habit.id,
            completion_date: dateStr,
            is_completed: true,
            target_achieved: response.target_achieved || true,
            achievement_percentage: response.achievement_percentage || 100
          };
          setCompletions(prev => [newCompletion, ...prev]);
        } else {
          // Remove completion
          setCompletions(prev => prev.filter(c => !c.completion_date.startsWith(dateStr)));
        }
      } else {
        setError(response.message || 'Failed to update completion');
      }
    } catch (err) {
      console.error('Error updating completion:', err);
      setError('Failed to update completion. Please try again.');
    }
  };

  const handleSaveCompletionValue = async (count: number | undefined, measurement: number | undefined) => {
    if (!user?.id || !completionModalState || !habit) return;

    try {
      const { date } = completionModalState;

      const completionData: RecordCompletionData = {
        habit_id: habit.id,
        user_id: user.id,
        completion_date: date,
        is_completed: count !== undefined || measurement !== undefined,
        completion_count: count,
        measurement_value: measurement
      };

      const response = await habitService.recordCompletion(completionData);

      if (response.success) {
        const isCompleted = count !== undefined || measurement !== undefined;

        // Update calendar data
        setCalendarData(prev => {
          const newData = { ...prev };
          if (isCompleted) {
            newData[date] = true;
          } else {
            delete newData[date];
          }
          return newData;
        });

        // Update completions list
        if (isCompleted) {
          // Check if we're updating an existing completion or adding a new one
          const existingIndex = completions.findIndex(c => c.completion_date.startsWith(date));

          if (existingIndex >= 0) {
            // Update existing
            setCompletions(prev => {
              const newCompletions = [...prev];
              newCompletions[existingIndex] = {
                ...newCompletions[existingIndex],
                completion_count: count,
                measurement_value: measurement,
                target_achieved: response.target_achieved || false,
                achievement_percentage: response.achievement_percentage || 0
              };
              return newCompletions;
            });
          } else {
            // Add new completion
            const newCompletion: HabitCompletion = {
              id: Date.now(), // Temporary ID
              habit_id: habit.id,
              completion_date: date,
              is_completed: true,
              completion_count: count,
              measurement_value: measurement,
              target_achieved: response.target_achieved || false,
              achievement_percentage: response.achievement_percentage || 0
            };
            setCompletions(prev => [newCompletion, ...prev]);
          }
        } else {
          // Remove completion if it was cleared
          setCompletions(prev => prev.filter(c => !c.completion_date.startsWith(date)));
        }

        setCompletionModalState(null);
      } else {
        setError(response.message || 'Failed to update habit completion');
      }
    } catch (err) {
      console.error('Error saving completion value:', err);
      setError('Failed to save completion value. Please try again.');
    }
  };

  const handlePrevMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + 1);
      return newMonth;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateToString = (year: number, month: number, day: number) => {
    // Create date string without timezone issues
    const yearStr = year.toString();
    const monthStr = (month + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return `${yearStr}-${monthStr}-${dayStr}`;
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 80) return '#4CAF50';
    if (rate >= 60) return '#FF9800';
    return '#f44336';
  };

  const getFrequencyDisplayText = (frequencyType: string, frequencyValue: number, frequencyUnit: string) => {
    switch (frequencyType) {
      case 'daily':
        return 'Every day';
      case 'every_x_days':
        return `Every ${frequencyValue} day${frequencyValue > 1 ? 's' : ''}`;
      case 'weekly':
        return `${frequencyValue} time${frequencyValue > 1 ? 's' : ''} per week`;
      case 'monthly':
        return `${frequencyValue} time${frequencyValue > 1 ? 's' : ''} per month`;
      case 'custom_interval':
        return `${frequencyValue} time${frequencyValue > 1 ? 's' : ''} in ${frequencyValue} day${frequencyValue > 1 ? 's' : ''}`;
      default:
        return `${frequencyValue} ${frequencyType}`;
    }
  };


  const renderCircularProgress = (percentage: number) => {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="circular-progress">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r={radius}
            stroke="#f0f0f0"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            stroke="#333333"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 60 60)"
            className="progress-circle"
          />
        </svg>
        <div className="progress-text">{percentage}%</div>
      </div>
    );
  };

  const renderLineChart = () => {
    if (chartData.length === 0) return <div className="no-data">No data available</div>;

    const maxScore = Math.max(...chartData.map(d => d.score), 100);
    const minScore = Math.min(...chartData.map(d => d.score), 0);
    const chartHeight = 200;
    const padding = 20;

    const points = chartData.map((point, index) => {
      const x = (index / (chartData.length - 1)) * (100 - 2 * (padding / 100) * 100) + (padding / 100) * 100;
      const y = 100 - (padding / 100) * 100 - ((point.score - minScore) / (maxScore - minScore)) * (100 - 2 * (padding / 100) * 100);
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="line-chart">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: chartHeight }}>
          {/* Chart line */}
          <polyline
            points={points}
            fill="none"
            stroke="#333333"
            strokeWidth="0.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Data points */}
          {chartData.map((point, index) => {
            const x = (index / (chartData.length - 1)) * (100 - 2 * (padding / 100) * 100) + (padding / 100) * 100;
            const y = 100 - (padding / 100) * 100 - ((point.score - minScore) / (maxScore - minScore)) * (100 - 2 * (padding / 100) * 100);
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="1"
                fill="#333333"
              />
            );
          })}

          {/* X-axis labels for line chart */}
          {chartData.map((point, index) => {
            const x = (index / (chartData.length - 1)) * (100 - 2 * (padding / 100) * 100) + (padding / 100) * 100;
            const date = new Date(point.date);
            let label;

            if (selectedPeriod === 'week') {
              // For weekly view, show compact format (e.g., "Dec 1-7" or just "1-7" if same month)
              const weekStart = new Date(date);
              const weekEnd = new Date(date);
              weekEnd.setDate(weekEnd.getDate() + 6);

              const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' });
              const startDay = weekStart.getDate();
              const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
              const endDay = weekEnd.getDate();

              if (startMonth === endMonth) {
                // Only show month once if it's the same month
                label = `${startMonth} ${startDay}-${endDay}`;
              } else {
                // Show abbreviated format for cross-month weeks
                label = `${startMonth} ${startDay}-${endMonth} ${endDay}`;
              }
            } else if (selectedPeriod === 'month') {
              // For monthly view, show just month and year
              label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            }

            return (
              <text
                key={index}
                x={x}
                y="97"
                fill="#666666"
                fontSize="2.5"
                textAnchor="middle"
                style={{
                  transform: chartData.length > 6 ? 'rotate(-45deg)' : 'none',
                  transformOrigin: `${x}% 97%`
                }}
              >
                {label}
              </text>
            );
          })}
        </svg>
      </div>
    );
  };

  const renderBarChart = () => {
    if (historyData.length === 0) return <div className="no-data">No data available</div>;

    // Format data for Recharts
    const chartData = historyData.map((point) => {
      const date = new Date(point.date);
      let label;

      if (selectedPeriod === 'week') {
        const weekStart = new Date(date);
        const weekEnd = new Date(date);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' });
        const startDay = weekStart.getDate();
        const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
        const endDay = weekEnd.getDate();

        if (startMonth === endMonth) {
          label = `${startMonth} ${startDay}-${endDay}`;
        } else {
          label = `${startMonth} ${startDay}-${endMonth} ${endDay}`;
        }
      } else if (selectedPeriod === 'month') {
        label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }

      return {
        name: label,
        completions: point.completions
      };
    });

    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="name"
            tick={{ fill: '#666666', fontSize: 12 }}
            angle={chartData.length > 4 ? -45 : 0}
            textAnchor={chartData.length > 4 ? 'end' : 'middle'}
            height={chartData.length > 4 ? 60 : 30}
          />
          <YAxis
            tick={{ fill: '#666666', fontSize: 12 }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: '0.25rem',
              fontSize: '0.875rem'
            }}
          />
          <Bar dataKey="completions" fill="#333333" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderMeasurementChart = () => {
    if (measurementData.length === 0 || measurementData.every(d => d.value === 0)) {
      return <div className="no-data">No data available</div>;
    }

    // Format data for Recharts
    const chartData = measurementData.map((point) => {
      const date = new Date(point.date);
      let label;

      if (selectedPeriod === 'week') {
        const weekStart = new Date(date);
        const weekEnd = new Date(date);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' });
        const startDay = weekStart.getDate();
        const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
        const endDay = weekEnd.getDate();

        if (startMonth === endMonth) {
          label = `${startMonth} ${startDay}-${endDay}`;
        } else {
          label = `${startMonth} ${startDay}-${endMonth} ${endDay}`;
        }
      } else if (selectedPeriod === 'month') {
        label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }

      return {
        name: label,
        value: point.value
      };
    });

    console.log('Measurement data for chart:', measurementData);
    console.log('Formatted chart data:', chartData);

    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="name"
            tick={{ fill: '#666666', fontSize: 12 }}
            angle={chartData.length > 4 ? -45 : 0}
            textAnchor={chartData.length > 4 ? 'end' : 'middle'}
            height={chartData.length > 4 ? 60 : 30}
          />
          <YAxis
            tick={{ fill: '#666666', fontSize: 12 }}
            domain={[0, 'auto']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: '0.25rem',
              fontSize: '0.875rem'
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#333333"
            strokeWidth={2}
            dot={{ fill: '#333333', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderCompactCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    const daysInMonth = endDate.getDate();
    const today = new Date();

    const calendarDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateToString(year, month, day);
      const date = new Date(year, month, day);
      const isCompleted = calendarData[dateStr] || false;
      const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
      const isFuture = date > today;


      calendarDays.push(
        <div
          key={day}
          className={`calendar-day compact ${isCompleted ? 'completed' : ''} ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''}`}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="compact-calendar">
        <div className="calendar-header">
          <button className="nav-btn compact" onClick={handlePrevMonth}>‹</button>
          <h4>{selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
          <button className="nav-btn compact" onClick={handleNextMonth}>›</button>
        </div>
        <div className="calendar-weekdays">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="weekday-label">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {/* Empty cells for days before month start */}
          {Array.from({ length: startDate.getDay() }, (_, i) => (
            <div key={`empty-${i}`} className="calendar-day empty"></div>
          ))}
          {calendarDays}
        </div>
      </div>
    );
  };

  const renderFullCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    const daysInMonth = endDate.getDate();
    const today = new Date();

    const calendarDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateToString(year, month, day);
      const date = new Date(year, month, day);
      const isCompleted = calendarData[dateStr] || false;
      const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
      const isFuture = date > today;

      calendarDays.push(
        <div
          key={day}
          className={`calendar-day full ${isCompleted ? 'completed' : ''} ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''}`}
          onClick={() => !isFuture && handleCalendarDayClick(dateStr)}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="full-calendar">
        <div className="calendar-nav">
          <button className="nav-btn" onClick={handlePrevMonth}>‹</button>
          <h3>{selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
          <button className="nav-btn" onClick={handleNextMonth}>›</button>
        </div>
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
            <div key={index} className="weekday-label full">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {/* Empty cells for days before month start */}
          {Array.from({ length: startDate.getDay() }, (_, i) => (
            <div key={`empty-${i}`} className="calendar-day empty"></div>
          ))}
          {calendarDays}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="habit-detail">
        <header className="habit-detail-header">
          <div className="habit-detail-header-left">
            <button className="habit-detail-menu-btn" onClick={onToggleSidebar}>☰</button>
            <button className="habit-detail-back-btn" onClick={() => navigate(-1)}>
              ←
            </button>
          </div>
          <h1>Habit Details</h1>
        </header>
        <div className="habit-detail-content">
          <div className="loading-state">
            <p>Loading habit details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !habit) {
    return (
      <div className="habit-detail">
        <header className="habit-detail-header">
          <div className="habit-detail-header-left">
            <button className="habit-detail-back-btn" onClick={() => navigate(-1)}>
              ←
            </button>
          </div>
          <h1>Habit Details</h1>
        </header>
        <div className="habit-detail-content">
          <div className="error-state">
            <p>{error || 'Habit not found'}</p>
            <button onClick={() => navigate('/habits')} className="back-to-habits-btn">
              Back to Habits
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="habit-detail">
      <header className="habit-detail-header">
        <div className="habit-detail-header-left">
          <button className="habit-detail-back-btn" onClick={() => navigate(-1)}>
            ←
          </button>
        </div>
        <h1>Habit Details</h1>
        <div className="habit-detail-header-right">
          <button className="habit-detail-edit-btn" onClick={() => navigate(`/habits/${id}/edit`)}>
            Edit
          </button>
          <button className="habit-detail-delete-btn" onClick={handleDeleteHabit}>
            Delete
          </button>
        </div>
      </header>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="habit-detail-content">
        {/* Habit Header */}
        <div className="habit-detail-header-section">
          <div className="habit-detail-icon-large">{habit.icon}</div>
          <div className="habit-detail-info">
            <h2 className="habit-detail-name">{habit.name}</h2>
            <div className="habit-detail-meta">
              <span className="habit-detail-category">{categories.find(c => c.value === habit.category)?.label}</span>
              <span className="habit-detail-frequency">
                {getFrequencyDisplayText(habit.frequency_type, habit.frequency_value, habit.frequency_unit)}
              </span>
            </div>
          </div>
          <div className="habit-detail-status">
            <div className="status-switch-container">
              <span className="status-switch-label">Active</span>
              <label className="status-switch">
                <input
                  type="checkbox"
                  checked={habit.is_active}
                  onChange={handleToggleStatus}
                />
                <span className="status-switch-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <div className="overview-section">
          <h3 className="section-title">Overview</h3>
          <div className="overview-content">
            <div className="overview-progress">
              {renderCircularProgress(detailedStats ? Math.round(detailedStats.full_score * 100) : 0)}
            </div>
            <div className="overview-stats">
              <div className="overview-stat">
                <div className="stat-value">{detailedStats ? Math.round(detailedStats.full_score * 100) : 0}%</div>
                <div className="stat-label">Full Score</div>
              </div>
              <div className="overview-stat">
                <div className="stat-value">{detailedStats ? Math.round(detailedStats.month_score * 100) : 0}%</div>
                <div className="stat-label">Month Score</div>
              </div>
              <div className="overview-stat">
                <div className="stat-value">{detailedStats ? (detailedStats.month_change >= 0 ? '+' : '') + Math.round(detailedStats.month_change * 100) + '%' : '0%'}</div>
                <div className="stat-label">Month Change</div>
              </div>
              <div className="overview-stat">
                <div className="stat-value">{detailedStats ? detailedStats.total_completions : (streak ? streak.total_completions : 0)}</div>
                <div className="stat-label">Total</div>
              </div>
            </div>
          </div>
        </div>


        {/* History Section */}
        <div className="chart-section">
          <div className="section-header">
            <h3 className="section-title">History</h3>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as 'week' | 'month')}
              className="period-selector"
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
          <div className="chart-container">
            {renderBarChart()}
          </div>
        </div>

        {/* Measurement Trend Section (only for measurement habits) */}
        {(habit.tracking_type === 'measurement' || habit.tracking_type === 'both') && (
          <div className="chart-section">
            <div className="section-header">
              <h3 className="section-title">Measurement Trend</h3>
            </div>
            <div className="chart-container">
              {renderMeasurementChart()}
            </div>
          </div>
        )}

        {/* Calendar Section */}
        <div className="chart-section">
          <div className="section-header">
            <h3 className="section-title">Calendar</h3>
            <button
              className="edit-calendar-btn"
              onClick={() => setShowCalendarEdit(!showCalendarEdit)}
            >
              {showCalendarEdit ? 'Done' : 'Edit'}
            </button>
          </div>
          <div className="calendar-container">
            {showCalendarEdit ? renderFullCalendar() : renderCompactCalendar()}
          </div>
          {showCalendarEdit && (
            <div className="calendar-instructions">
              <p>Click on past days to mark/unmark completions. Future days cannot be edited.</p>
            </div>
          )}
        </div>


      </div>
      {/* Completion Value Modal */}
      {completionModalState && (() => {
        // Find existing completion for this date if any
        const completion = completions.find(c => c.completion_date.startsWith(completionModalState.date));

        return (
          <CompletionValueModal
            habit={completionModalState.habit}
            date={completionModalState.date}
            currentValue={completion?.completion_count}
            currentMeasurement={completion?.measurement_value}
            onSave={handleSaveCompletionValue}
            onClose={() => setCompletionModalState(null)}
          />
        );
      })()}
    </div>
  );
};

export default HabitDetail;
